import { FC, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Organization {
  id: string;
  name: string;
}

interface DirectAllocationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  batteryId: string;
  batteryName: string;
}

export const DirectAllocationModal: FC<DirectAllocationModalProps> = ({
  open,
  onClose,
  onSuccess,
  batteryId,
  batteryName
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      fetchOrganizations();
    }
  }, [open]);

  const fetchOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .eq('classification', 'klant')
        .order('name');

      if (error) throw error;
      setOrganizations(data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast({
        title: 'Fout bij ophalen klanten',
        description: 'Kon klanten niet laden',
        variant: 'destructive'
      });
    }
  };

  const handleAllocate = async () => {
    if (!selectedOrgId) {
      toast({
        title: 'Selecteer een klant',
        description: 'Kies een klant om de batterij aan toe te wijzen',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Create allocation
      const { error: allocationError } = await supabase
        .from('battery_allocations')
        .insert({
          battery_id: batteryId,
          org_id: selectedOrgId,
          status: 'confirmed',
          notes,
          confirmed_at: new Date().toISOString()
        });

      if (allocationError) throw allocationError;

      // Update battery status
      const { error: batteryError } = await supabase
        .from('batteries')
        .update({ status: 'gereserveerd' })
        .eq('id', batteryId);

      if (batteryError) throw batteryError;

      toast({
        title: 'Batterij toegewezen',
        description: `${batteryName} is toegewezen aan de geselecteerde klant`
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error allocating battery:', error);
      toast({
        title: 'Fout bij toewijzen',
        description: 'Kon batterij niet toewijzen',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedOrgId('');
    setNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Batterij direct toewijzen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-4">
              Wijs {batteryName} direct toe aan een klant
            </p>
          </div>

          <div className="space-y-2">
            <Label>Klant *</Label>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecteer klant" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Notities</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Eventuele notities..."
              rows={3}
            />
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Annuleren
            </Button>
            <Button onClick={handleAllocate} disabled={loading}>
              {loading ? 'Toewijzen...' : 'Wijs toe'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
